// NOTA: SearchOverlay no usa <Modal> shared porque es un command-palette pattern,
// no un diálogo. Posicionamiento top:12% (no centered), focus + keyboard nav propio,
// sin botones de footer. Si aparece otro use case (búsqueda contextual), promover a
// primitive <CommandPalette> en el DS. Excepción documentada en el sprint DS adoption.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobalSearch } from '../hooks/useSearch.js';
import { Icon, type IconName } from './Icon.js';
import { EmptyState } from './ui/EmptyState.js';

interface Props {
  onClose: () => void;
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: '6px 16px 4px',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        color: 'var(--evo-fg-4)',
        borderTop: '1px solid var(--evo-stroke)',
      }}
    >
      {label}
    </div>
  );
}

function ResultRow({
  icon, primary, secondary, tertiary, active, onClick,
}: {
  icon: IconName; primary: string; secondary?: string | null; tertiary?: string | null;
  active: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-2.5 text-left"
      style={{
        background: active ? 'var(--evo-bg-2)' : 'transparent',
        border: 'none',
        borderLeft: `2px solid ${active ? 'var(--evo-cyan)' : 'transparent'}`,
        cursor: 'pointer',
        transition: 'background 0.1s',
        width: '100%',
      }}
    >
      <div style={{ paddingTop: 2, color: 'var(--evo-fg-3)', flexShrink: 0 }}>
        <Icon name={icon} size={13} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-sm truncate" style={{ color: active ? 'var(--evo-cyan)' : 'var(--evo-fg-1)', fontWeight: 500 }}>
          {primary}
        </div>
        {secondary && (
          <div className="text-xs truncate mt-0.5" style={{ color: 'var(--evo-fg-3)' }}>
            {secondary}
          </div>
        )}
        {tertiary && (
          <div className="text-xs truncate" style={{ color: 'var(--evo-fg-4)' }}>
            {tertiary}
          </div>
        )}
      </div>
    </button>
  );
}

export function SearchOverlay({ onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => { setCursor(0); }, [debouncedQ]);

  const { data, isFetching } = useGlobalSearch(debouncedQ);
  const emails = data?.emails ?? [];
  const contacts = data?.contacts ?? [];
  const leads = data?.leads ?? [];
  const reports = data?.reports ?? [];

  // Flatten all items for keyboard nav
  type NavItem = { kind: 'email' | 'contact' | 'lead' | 'report'; id: number | string };
  const flat: NavItem[] = [
    ...emails.map(e => ({ kind: 'email' as const, id: e.id })),
    ...contacts.map(c => ({ kind: 'contact' as const, id: c.id })),
    ...leads.map(l => ({ kind: 'lead' as const, id: l.id })),
    ...reports.map(r => ({ kind: 'report' as const, id: r.id })),
  ];
  const hasResults = flat.length > 0;
  const isEmpty = debouncedQ.length >= 2 && !isFetching && !hasResults;

  function navigate_to(item: NavItem) {
    onClose();
    if (item.kind === 'email') navigate(`/email?id=${String(item.id)}`);
    else if (item.kind === 'contact') navigate(`/contacts?selected=${String(item.id)}`);
    else if (item.kind === 'lead') navigate(`/leads/${String(item.id)}`);
    else navigate(`/reports/${String(item.id)}`);
  }

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, flat.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
      if (e.key === 'Enter' && flat[cursor]) navigate_to(flat[cursor]);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat, cursor, onClose]);

  // absolute cursor index relative to each section
  const emailOffset = 0;
  const contactOffset = emails.length;
  const leadOffset = contactOffset + contacts.length;
  const reportOffset = leadOffset + leads.length;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, backdropFilter: 'blur(2px)' }}
      />
      <div
        style={{
          position: 'fixed',
          top: '12%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(660px, 92vw)',
          background: 'var(--evo-panel)',
          border: '1px solid var(--evo-stroke)',
          borderRadius: '12px',
          zIndex: 201,
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '72vh',
          overflow: 'hidden',
        }}
      >
        {/* Input */}
        <div
          className="flex items-center gap-3 px-4"
          style={{ borderBottom: '1px solid var(--evo-stroke)', minHeight: 52, flexShrink: 0 }}
        >
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t('globalSearch.placeholder')}
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              fontSize: '15px', color: 'var(--evo-fg)', fontFamily: 'inherit',
            }}
          />
          {isFetching && (
            <span style={{ color: 'var(--evo-fg-3)', fontSize: 11, flexShrink: 0 }}>
              {t('globalSearch.loading')}
            </span>
          )}
          <kbd style={{ flexShrink: 0, fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--evo-bg-2)', color: 'var(--evo-fg-3)', border: '1px solid var(--evo-stroke)' }}>
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {isEmpty && (
            <EmptyState title={t('globalSearch.noResults')} size="sm" reserveSpace />
          )}

          {emails.length > 0 && (
            <>
              <SectionHeader label={t('globalSearch.emails')} />
              {emails.map((e, i) => (
                <ResultRow
                  key={`email-${e.id}`}
                  icon="envelope-large"
                  primary={e.subject ?? '(sin asunto)'}
                  secondary={e.from}
                  tertiary={e.preview}
                  active={cursor === emailOffset + i}
                  onClick={() => navigate_to({ kind: 'email', id: e.id })}
                />
              ))}
            </>
          )}

          {contacts.length > 0 && (
            <>
              <SectionHeader label={t('globalSearch.contacts')} />
              {contacts.map((c, i) => (
                <ResultRow
                  key={`contact-${c.id}`}
                  icon="contacts"
                  primary={c.fullName ?? c.primaryEmail ?? String(c.id)}
                  secondary={c.primaryEmail}
                  tertiary={c.businessName}
                  active={cursor === contactOffset + i}
                  onClick={() => navigate_to({ kind: 'contact', id: c.id })}
                />
              ))}
            </>
          )}

          {leads.length > 0 && (
            <>
              <SectionHeader label={t('globalSearch.leads')} />
              {leads.map((l, i) => (
                <ResultRow
                  key={`lead-${l.id}`}
                  icon="bolt-large"
                  primary={l.businessName}
                  secondary={[l.city, l.category].filter(Boolean).join(' · ') || null}
                  tertiary={l.pipelineStage}
                  active={cursor === leadOffset + i}
                  onClick={() => navigate_to({ kind: 'lead', id: l.id })}
                />
              ))}
            </>
          )}

          {reports.length > 0 && (
            <>
              <SectionHeader label={t('globalSearch.reports')} />
              {reports.map((r, i) => (
                <ResultRow
                  key={`report-${r.id}`}
                  icon="activity-large"
                  primary={r.title}
                  secondary={r.type}
                  active={cursor === reportOffset + i}
                  onClick={() => navigate_to({ kind: 'report', id: r.id })}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}
